





























































































































import {
  ComponentInternalInstance,
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  PropType,
  Ref,
  ref,
  watch,
} from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import { CoSyncCenterEvent } from '@/renderer/scripts/CoSync/SyncCenter/CoSyncCenterEvent';
import {
  CmdTypeFromRender,
  FetchOperateTaskListParam,
  FetchOperateTaskListResult,
} from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromRenderer';
import { CmdTypeFromWorker } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromWorker';
import { CmdFromSyncTask, SyncTaskUIStatus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenter';
import {
  CoSyncOperateStatus,
  CoSyncResultCode,
  CoSyncRunningSubStatus,
  CoSyncStatus,
  CoSynStatusInfo,
  OperateStatusInfo,
  SyncStatusResultInfo,
  SyncStatusRunningInfo,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncEvent';
import { OperateTaskInfo } from '@/workerProcessRenderer/scripts/CoSyncEngine/Operator/typings';
import { syncCenterEventBus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenterEventBus';
import { CoSyncReporter } from '@/renderer/views/desktop/FileManagement/FileSync/composition/CoSyncReporter';
import useSyncTaskControlHandler from '@/renderer/views/desktop/FileManagement/FileSync/composition/SyncTaskControlHandler';
import { SyncOperationType } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/SyncOperationItem';

import {
  CoSyncOperateDrawerItemStatus,
  getCoSyncOperateItemStatus,
} from '@/renderer/views/desktop/FileManagement/FileSync/composition/CoSyncOperateDrawerAdaper';

const MAX_LIST_SIZE = 200;

type ListItem = {
  operateTaskId: number;
  fileName: string;
  isDir: boolean;
  path: string;
  status: CoSyncOperateDrawerItemStatus;
  statusWording: string;
  progress: number;
  speedInBytePerSecond?: number;
  itemKey: string;
};

function buildSpeedWording(speedInBytePerSecond: number): string {
  const size = buildSize(speedInBytePerSecond);
  return `${size}/s`;
}

function buildSize(sizeInByte: number): string {
  if (sizeInByte < 1024) return `${sizeInByte.toFixed(1)}B`;
  if (sizeInByte < 1024 * 1024) return `${(sizeInByte / 1024).toFixed(1)}K`;
  if (sizeInByte < 1024 * 1024 * 1024) return `${(sizeInByte / 1024 / 1024).toFixed(1)}M`;
  if (sizeInByte < 1024 * 1024 * 1024 * 1024) return `${(sizeInByte / 1024 / 1024 / 1024).toFixed(1)}G`;
  return `${(sizeInByte / 1024 / 1024 / 1024 / 1024).toFixed(1)}T`;
}

function getIsDirFromOperateion(operateion: SyncOperationType, syncNodeIsDir = false): boolean {
  if (
    operateion === SyncOperationType.DELETE_REMOTE_IGNORED_EXIT ||
    operateion === SyncOperationType.DELETE_LOCAL_IGNORED_EXIT
  ) {
    return syncNodeIsDir;
  }

  switch (operateion) {
    case SyncOperationType.DWONLOAD:
    case SyncOperationType.UPLOAD:
    case SyncOperationType.DELETE_REMOTE:
    case SyncOperationType.DELETE_LOCAL:
    case SyncOperationType.MOVE_LOCAL:
    case SyncOperationType.MOVE_REMOTE:
    case SyncOperationType.UPDATE_TREE_NODE:
    case SyncOperationType.DELETE_TREE_NODE:
    case SyncOperationType.RNAME_LOCAL:
    case SyncOperationType.RNAME_REMOTE:
    case SyncOperationType.MOVE_TREE_NODE:
    case SyncOperationType.SKIP_INVALID_FILENAME:
      return false;

    case SyncOperationType.CREATE_LOCAL_FOLDER:
    case SyncOperationType.CREATE_REMOTE_FOLDER:
    case SyncOperationType.DELETE_REMOTE_FOLDER:
    case SyncOperationType.DELETE_LOCAL_FOLDER:
    case SyncOperationType.MOVE_LOCAL_FOLDER:
    case SyncOperationType.MOVE_REMOTE_FOLDER:
    case SyncOperationType.RNAME_LOCAL_FOLDER:
    case SyncOperationType.RNAME_REMOTE_FOLDER:
      return true;

    default: {
      const _exhaustiveCheck: never = operateion;
      return _exhaustiveCheck;
    }
  }
}

function buildItemFromOperateTaskInfo(taskInfo: OperateTaskInfo): ListItem | undefined {
  if (!taskInfo.taskId) return undefined;

  const itemKey = `${taskInfo.taskId}_${taskInfo.status}`;
  const isDir = getIsDirFromOperateion(taskInfo.item.operation, taskInfo.item.syncNode?.isDir ?? false);
  const status = getCoSyncOperateItemStatus(taskInfo);
  if (!status) return undefined;

  switch (taskInfo.status) {
    case CoSyncOperateStatus.TYPE_INIT:
    case CoSyncOperateStatus.TYPE_WAILTING:
      return {
        operateTaskId: taskInfo.taskId,
        fileName: taskInfo.name,
        isDir,
        path: taskInfo.item.path,
        status,
        statusWording: i18n_t('等待中'),
        progress: 0,
        itemKey,
      };

    case CoSyncOperateStatus.TYPE_RUNNING:
    case CoSyncOperateStatus.TYPE_START:
    case CoSyncOperateStatus.TYPE_HASH_COMPUTE: {
      let loadedWording = '';
      if (taskInfo.loadedSize && taskInfo.totalSize) {
        const loadedSizeWording = buildSize(taskInfo.loadedSize);
        const totalSizeWording = buildSize(taskInfo.totalSize);
        loadedWording = `${loadedSizeWording}/${totalSizeWording}`;
      }
      let statusWording = i18n_t('同步中{loadedWording}', { loadedWording });
      if (taskInfo.status === CoSyncOperateStatus.TYPE_HASH_COMPUTE) {
        statusWording = i18n_t('秒传中{loadedWording}', { loadedWording });
      }
      return {
        operateTaskId: taskInfo.taskId,
        fileName: taskInfo.name,
        isDir,
        path: taskInfo.item.path,
        status,
        statusWording,
        progress: taskInfo.progress ? Math.round(taskInfo.progress * 100) : 0,
        speedInBytePerSecond: taskInfo.speed,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_FINISH: {
      if (taskInfo.result === undefined) return undefined;
      if (taskInfo.result === CoSyncResultCode.CODE_SUCCESS) {
        return {
          operateTaskId: taskInfo.taskId,
          fileName: taskInfo.name,
          isDir,
          path: taskInfo.item.path,
          status,
          statusWording: i18n_t('同步成功'),
          progress: taskInfo.progress ? Math.round(taskInfo.progress * 100) : 100,
          itemKey,
        };
      }
      return {
        operateTaskId: taskInfo.taskId,
        fileName: taskInfo.name,
        isDir,
        path: taskInfo.item.path,
        status,
        statusWording: i18n_t('同步失败 {slot1}', { slot1: taskInfo.error ?? '' }),
        progress: taskInfo.progress ? Math.round(taskInfo.progress * 100) : 100,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_STOP: {
      return {
        operateTaskId: taskInfo.taskId,
        fileName: taskInfo.name,
        isDir,
        path: taskInfo.item.path,
        status,
        statusWording: i18n_t('已停止'),
        progress: taskInfo.progress ? Math.round(taskInfo.progress * 100) : 0,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_UPDATE_TREE:
    case CoSyncOperateStatus.TYPE_GROUP_FINISH:
    case CoSyncOperateStatus.TYPE_OPERATE_FINISH: {
      return undefined;
    }

    default: {
      const _exhaustiveCheck: never = taskInfo.status;
      return _exhaustiveCheck;
    }
  }
}

function buildItemFromTaskStatus(taskStatusInfo: OperateStatusInfo): ListItem | undefined {
  const itemKey = `${taskStatusInfo.taskId}_${taskStatusInfo.status}`;
  let isDir = false;
  if ('operation' in taskStatusInfo) {
    isDir = taskStatusInfo.isDir ?? getIsDirFromOperateion(taskStatusInfo.operation);
  }

  switch (taskStatusInfo.status) {
    case CoSyncOperateStatus.TYPE_START:
      return {
        operateTaskId: taskStatusInfo.taskId,
        fileName: taskStatusInfo.name,
        isDir,
        path: taskStatusInfo.relativePath,
        status: 'running',
        statusWording: i18n_t('同步中'),
        progress: 0,
        itemKey,
      };

    case CoSyncOperateStatus.TYPE_RUNNING: {
      let loadedWording = '';
      if (taskStatusInfo.loadedSize && taskStatusInfo.totalSize) {
        const loadedSizeWording = buildSize(taskStatusInfo.loadedSize);
        const totalSizeWording = buildSize(taskStatusInfo.totalSize);
        loadedWording = `${loadedSizeWording}/${totalSizeWording}`;
      }
      return {
        operateTaskId: taskStatusInfo.taskId,
        fileName: taskStatusInfo.name,
        isDir,
        path: taskStatusInfo.relativePath,
        status: 'running',
        statusWording: i18n_t('同步中 {loadedWording}', { loadedWording }),
        progress: taskStatusInfo.progress ? Math.round(taskStatusInfo.progress * 100) : 0,
        speedInBytePerSecond: taskStatusInfo.speed,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_FINISH: {
      if (taskStatusInfo.result === CoSyncResultCode.CODE_SUCCESS) {
        return {
          operateTaskId: taskStatusInfo.taskId,
          fileName: taskStatusInfo.name,
          isDir,
          path: taskStatusInfo.relativePath,
          status: 'success',
          statusWording: i18n_t('同步成功'),
          progress: taskStatusInfo.progress ? Math.round(taskStatusInfo.progress * 100) : 100,
          itemKey,
        };
      }

      return {
        operateTaskId: taskStatusInfo.taskId,
        fileName: taskStatusInfo.name,
        isDir,
        path: taskStatusInfo.relativePath,
        status: 'error',
        statusWording: i18n_t('同步失败 {slot1}', { slot1: taskStatusInfo.error }),
        progress: taskStatusInfo.progress ? Math.round(taskStatusInfo.progress * 100) : 100,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_STOP: {
      return {
        operateTaskId: taskStatusInfo.taskId,
        fileName: taskStatusInfo.name,
        isDir,
        path: taskStatusInfo.relativePath,
        status: 'stopped',
        statusWording: i18n_t('已停止'),
        progress: 100,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_HASH_COMPUTE: {
      return {
        operateTaskId: taskStatusInfo.taskId,
        fileName: taskStatusInfo.name,
        isDir,
        path: taskStatusInfo.relativePath,
        status: 'running',
        statusWording: i18n_t('秒传中'),
        progress: taskStatusInfo.progress,
        itemKey,
      };
    }

    case CoSyncOperateStatus.TYPE_UPDATE_TREE:
    case CoSyncOperateStatus.TYPE_GROUP_FINISH:
    case CoSyncOperateStatus.TYPE_OPERATE_FINISH: {
      break;
    }

    default: {
      const _exhaustiveCheck: never = taskStatusInfo;
      return _exhaustiveCheck;
    }
  }
}

export default defineComponent({
  name: 'CoSyncOperateDrawer',
  components: {},
  props: {
    show: {
      required: true,
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: Number,
      default: 1,
    },
    taskId: {
      required: true,
      type: Number,
    },
    statusInfo: {
      type: Object as PropType<CoSynStatusInfo>,
      required: false,
    },
  },
  setup(props, context) {
    const currComponent = getCurrentInstance() as ComponentInternalInstance;
    const vm = currComponent.proxy;

    const { methods: taskControlMethods } = useSyncTaskControlHandler();

    const innerShow = computed({
      get: () => props.show,
      set: (val: boolean) => {
        context.emit('update:show', val);
      },
    });

    const loading = ref(false);
    const tabs = ref(1);

    const runningListSortedFunc = (lhs: ListItem, rhs: ListItem) => {
      if (lhs.operateTaskId < rhs.operateTaskId) return -1;
      if (lhs.operateTaskId > rhs.operateTaskId) return 1;
      return 0;
    };

    const runningList = ref<ListItem[]>([]);
    let runningMap = new Map<number, ListItem>();
    const waitingList = ref<ListItem[]>([]);
    let waitingMap = new Map<number, ListItem>();
    const successList = ref<ListItem[]>([]);
    let successMap = new Map<number, ListItem>();
    const stoppedList = ref<ListItem[]>([]);
    let stoppedMap = new Map<number, ListItem>();

    const tableColumns = [{ colKey: 'operate-status' }];

    const normalStatustableRef = ref();

    const truncateNormalStatusTableData = computed(() => {
      const truncated = runningList.value
        .concat(stoppedList.value, waitingList.value, successList.value)
        .slice(0, MAX_LIST_SIZE);

      if (normalStatustableRef.value) {
        const contentRef = normalStatustableRef.value.$refs.primaryTableRef.$refs.tableContentRef;
        const pos = contentRef.scrollTop;
        nextTick(() => {
          contentRef.scrollTop = pos;
        });
      }
      return truncated;
    });

    const exceptionStatusTableData = ref<ListItem[]>([]);
    let exceptionMap = new Map<number, ListItem>();

    const exceptionStatustableRef = ref();
    const truncateExceptionStatusTableData = computed(() => {
      if (exceptionStatustableRef.value) {
        const contentRef = exceptionStatustableRef.value.$refs.primaryTableRef.$refs.tableContentRef;
        const pos = contentRef.scrollTop;
        nextTick(() => {
          contentRef.scrollTop = pos;
        });
      }
      return exceptionStatusTableData.value.slice(0, MAX_LIST_SIZE);
    });

    const isAllTaskFinish = computed(() => runningList.value.length === 0 && waitingList.value.length === 0);

    // 所有任务数量
    const allTaskCount = ref(0);

    const totalSpeed = computed(() =>
      runningList.value
        .map(it => it.speedInBytePerSecond)
        .filter(it => it !== undefined)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map(it => it!)
        .reduce((accumulator, it) => accumulator + it, 0),
    );

    const totalSpeedWording = computed(() => {
      const sppedWording = buildSpeedWording(totalSpeed.value);
      return `${sppedWording}`;
    });

    const finishedCount = ref(0);

    const summeryWording = computed(() => {
      if (isAllTaskFinish.value) {
        return i18n_t('全部完成，共{slot1}项', { slot1: allTaskCount.value });
      }
      return i18n_t('当前已完成{slot1}项，共{slot2}项{slot3}', {
        slot1: finishedCount.value,
        slot2: allTaskCount.value,
        slot3:
          runningList.value.length + waitingList.value.length === 0 || totalSpeed.value === 0
            ? ''
            : `，${totalSpeedWording.value}`,
      });
    });

    // 拉取可能失败
    const fetchedExceptionCount = ref(0);

    // 异常任务数量（如果IPC拉取得到就用准确的，拉不到就用列表数量）
    const exceptionCount = computed(() =>
      fetchedExceptionCount.value !== 0 ? fetchedExceptionCount.value : exceptionStatusTableData.value.length,
    );
    const skippedFilesList = computed(() => {
      const statusInfo = props.statusInfo as SyncStatusResultInfo | undefined;
      return statusInfo?.skippedFiles ?? [];
    });
    const totalExceptionCount = computed(() => exceptionCount.value + skippedFilesList.value.length);

    const nonExceptionCount = computed(() => allTaskCount.value - exceptionCount.value);

    const operateStatusTitle = computed(() =>
      nonExceptionCount.value > 0 ? i18n_t('同步列表{slot1}', { slot1: nonExceptionCount.value }) : i18n_t('同步列表'),
    );

    const operateExceptionTitle = computed(() =>
      totalExceptionCount.value > 0
        ? i18n_t('同步异常{slot1}', { slot1: totalExceptionCount.value })
        : i18n_t('同步异常'),
    );

    function resetList() {
      runningList.value = [];
      runningMap.clear();

      waitingList.value = [];
      waitingMap.clear();

      successList.value = [];
      successMap.clear();

      stoppedList.value = [];
      stoppedMap.clear();

      exceptionStatusTableData.value = [];
      exceptionMap.clear();

      allTaskCount.value = 0;
      finishedCount.value = 0;
      fetchedExceptionCount.value = 0;
    }

    function setUpOperateStatus(fetchReuslt: FetchOperateTaskListResult) {
      const operateTaskInfoList: OperateTaskInfo[] = fetchReuslt.taskInfoList;
      allTaskCount.value = fetchReuslt.allCount;
      finishedCount.value = fetchReuslt.finishCount;
      fetchedExceptionCount.value = fetchReuslt.exceptionCount;

      const itemList = operateTaskInfoList
        .map(it => buildItemFromOperateTaskInfo(it))
        .filter(it => it !== undefined)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map(it => it!);

      const setUpListAndMap = (
        inputList: ListItem[],
        currList: Ref<ListItem[]>,
        status: 'running' | 'waiting' | 'success' | 'stopped' | 'error',
      ) => {
        const resList = currList.value.concat(inputList.filter(it => it.status === status));
        const resMap = new Map<number, ListItem>(resList.map(it => [it.operateTaskId, it]));
        return { list: resList, map: resMap };
      };

      const runningRes = setUpListAndMap(itemList, runningList, 'running');
      runningList.value = runningRes.list.sort(runningListSortedFunc);
      runningMap = runningRes.map;

      const waitingRes = setUpListAndMap(itemList, waitingList, 'waiting');
      console.log(
        'setUpOperateStatus waitingRes',
        waitingRes.list.length,
        itemList.filter(it => it.status === 'waiting').length,
      );

      waitingList.value = waitingRes.list;
      waitingMap = waitingRes.map;

      const succRes = setUpListAndMap(itemList, successList, 'success');
      successList.value = succRes.list;
      successMap = succRes.map;

      const stopRes = setUpListAndMap(itemList, stoppedList, 'stopped');
      stoppedList.value = stopRes.list;
      stoppedMap = stopRes.map;

      const exceptionRes = setUpListAndMap(itemList, exceptionStatusTableData, 'error');
      exceptionStatusTableData.value = exceptionRes.list;
      exceptionMap = exceptionRes.map;

      console.log('setUpOperateStatus', waitingList.value.length, waitingMap.size);
    }

    const onWorkerCmd = (params?: any) => {
      const payload = params as CmdFromSyncTask;
      if (payload.coSyncInfo.localSyncId !== props.taskId) return;
      if (payload.cmd.cmdType !== CmdTypeFromWorker.UPDATE_OPERATE_TASK_STATUS) return;

      const statusInfoList = payload.cmd.param as OperateStatusInfo[];
      logger.ui.info('onWorkerCmd', statusInfoList.length);

      const updateItemInMap = (map: Map<number, ListItem>, statusInfo: OperateStatusInfo) => {
        const listItem = buildItemFromTaskStatus(statusInfo);
        if (!listItem) return;
        map.set(statusInfo.taskId, listItem);
      };

      const deleteInAllMap = (statusInfo: OperateStatusInfo) => {
        runningMap.delete(statusInfo.taskId);
        waitingMap.delete(statusInfo.taskId);
        successMap.delete(statusInfo.taskId);
        exceptionMap.delete(statusInfo.taskId);
        stoppedMap.delete(statusInfo.taskId);
      };

      statusInfoList.forEach(statusInfo => {
        switch (statusInfo.status) {
          case CoSyncOperateStatus.TYPE_START:
          case CoSyncOperateStatus.TYPE_RUNNING:
          case CoSyncOperateStatus.TYPE_HASH_COMPUTE: {
            deleteInAllMap(statusInfo);
            updateItemInMap(runningMap, statusInfo);
            break;
          }

          case CoSyncOperateStatus.TYPE_FINISH: {
            if (statusInfo.result === CoSyncResultCode.CODE_SUCCESS) {
              deleteInAllMap(statusInfo);
              updateItemInMap(successMap, statusInfo);
            } else {
              deleteInAllMap(statusInfo);
              updateItemInMap(exceptionMap, statusInfo);
            }
            break;
          }

          case CoSyncOperateStatus.TYPE_STOP: {
            deleteInAllMap(statusInfo);
            updateItemInMap(stoppedMap, statusInfo);
            break;
          }

          case CoSyncOperateStatus.TYPE_UPDATE_TREE:
          case CoSyncOperateStatus.TYPE_GROUP_FINISH:
          case CoSyncOperateStatus.TYPE_OPERATE_FINISH: {
            break;
          }

          default: {
            const _exhaustiveCheck: never = statusInfo;
            return _exhaustiveCheck;
          }
        }
      });

      runningList.value = Array.from(runningMap.values()).sort(runningListSortedFunc);
      waitingList.value = Array.from(waitingMap.values());
      successList.value = Array.from(successMap.values());
      stoppedList.value = Array.from(stoppedMap.values());
      exceptionStatusTableData.value = Array.from(exceptionMap.values());

      const lastItem = statusInfoList.at(-1);
      if (lastItem?.finishTaskCount) {
        finishedCount.value = lastItem.finishTaskCount;
      }
      if (lastItem?.exceptionCount) {
        fetchedExceptionCount.value = lastItem.exceptionCount;
      }
      if (lastItem?.allTaskCount) {
        allTaskCount.value = lastItem?.allTaskCount;
      }
    };

    const onTaskStatusChange = async (syncTaskStatus: SyncTaskUIStatus) => {
      if (syncTaskStatus.coSyncInfo.localSyncId !== props.taskId) return;

      if (syncTaskStatus.statusInfo.status === CoSyncStatus.STATUS_RUNNING) {
        const info = syncTaskStatus.statusInfo as SyncStatusRunningInfo;
        if (info.subStatus === CoSyncRunningSubStatus.STATUS_RUNNING_SCANNER_START) {
          // 进入下一次扫描了，清理上一次的内容
          resetList();
        } else if (info.subStatus === CoSyncRunningSubStatus.STATUS_RUNNING_OPERATE_START) {
          // 进入下一次执行器了，可以获取到完整列表了
          loading.value = true;

          const fetchResult: FetchOperateTaskListResult = await taskControlMethods.fetchOperateTaskList(props.taskId, {
            firstNormalN: MAX_LIST_SIZE,
            firstExceptionN: MAX_LIST_SIZE,
          } as FetchOperateTaskListParam);
          setUpOperateStatus(fetchResult);

          taskControlMethods.applyNoneReturnCommand(props.taskId, {
            cmdType: CmdTypeFromRender.START_LISTEN_OPERATE_TASK,
          });

          loading.value = false;
        }
      }
    };

    const methods = {
      onDrawerClose() {
        innerShow.value = false;
      },
    };

    watch(
      () => innerShow.value,
      async val => {
        if (val) {
          resetList();

          tabs.value = props.defaultTab;

          CoSyncReporter.getInstance().reportOperateDrawerShowBegin();
          loading.value = true;

          syncCenterEventBus.on(CoSyncCenterEvent.onTaskStatusChange, onTaskStatusChange);
          syncCenterEventBus.on(CoSyncCenterEvent.onWorkerCmd, onWorkerCmd);

          taskControlMethods.applyNoneReturnCommand(props.taskId, {
            cmdType: CmdTypeFromRender.START_LISTEN_OPERATE_TASK,
          });

          const fetchResult: FetchOperateTaskListResult = await taskControlMethods.fetchOperateTaskList(props.taskId, {
            firstNormalN: MAX_LIST_SIZE,
            firstExceptionN: MAX_LIST_SIZE,
          } as FetchOperateTaskListParam);
          setUpOperateStatus(fetchResult);

          loading.value = false;
          CoSyncReporter.getInstance().reportOperateDrawerShowEnd(fetchResult.allCount);
        } else {
          CoSyncReporter.getInstance().reportOperateDrawerClose(loading.value);

          taskControlMethods.applyNoneReturnCommand(props.taskId, {
            cmdType: CmdTypeFromRender.STOP_LISTEN_OPERATE_TASK,
          });

          syncCenterEventBus.off(CoSyncCenterEvent.onWorkerCmd, onWorkerCmd);
          syncCenterEventBus.off(CoSyncCenterEvent.onTaskStatusChange, onTaskStatusChange);
        }
      },
    );

    return {
      innerShow,
      tabs,
      loading,

      operateStatusTitle,
      isAllTaskFinish,
      allTaskCount,
      summeryWording,

      operateExceptionTitle,
      exceptionCount,

      normalStatustableRef,
      exceptionStatustableRef,
      tableColumns,
      truncateNormalStatusTableData,
      exceptionStatusTableData,
      truncateExceptionStatusTableData,
      skippedFilesList,
      totalExceptionCount,

      onDrawerClose: methods.onDrawerClose,
    };
  },
});
